import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Buttons, FlexContainer } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { signInAction, forgotPasswordAction, setUserInfoAction } from 'Store/modules/auth/actions';
import { emailSelector } from 'Store/modules/auth/selectors';
import { authErrorSelector } from 'Store/modules/app/selectors';
import { setAuthErrorAction } from 'Store/modules/app/actions';
import { useTranslation } from 'Hooks/useTranslation';
import { AuthValuesType } from 'Types/common';
import * as Styled from './styles';

const initialState = {
  email: {
    value: '',
    isValid: false,
  },
  password: {
    value: '',
    isValid: false,
  },
};

const SignInForm = (): JSX.Element => {
  const dispatch = useDispatch();
  const authError = useSelector(authErrorSelector);
  const emailSaved = useSelector(emailSelector);
  const { t } = useTranslation(locizeNS.AUTH);
  const [values, setValues] = useState(initialState);

  useEffect(() => {
    if (values.email.isValid) {
      dispatch(setAuthErrorAction(null));
    }
  }, [values.email]);

  useEffect(() => {
    if (emailSaved) {
      setValues(prev => {
        return { ...prev, email: { ...prev.email, value: emailSaved, isValid: true } };
      });
    }
  }, [emailSaved]);

  const handleOnChange = ({ id, value }: AuthValuesType): void => {
    setValues(prev => {
      return { ...prev, [id]: { ...prev[id], ...value } };
    });
  };

  const onSaveEmail = (): void => {
    dispatch(setUserInfoAction({ user: { email: values.email.value } }));
  };

  const onForgotPassword = (): void => {
    onSaveEmail();
    dispatch(forgotPasswordAction({ email: values.email.value }));
  };

  const handleOnSubmit = (e): void => {
    const { email, password } = values;
    e.stopPropagation();
    e.preventDefault();
    onSaveEmail();
    dispatch(signInAction({ email: email.value, password: password.value }));
  };

  const isValidForm = useMemo(
    () => values.email.value !== '' && values.email.isValid && values.password.value !== '',
    [values]
  );

  return (
    <form onSubmit={handleOnSubmit} data-testid="form">
      <FlexContainer direction="column" gap="dimension_16">
        <Styled.Input
          value={values.email.value}
          onChange={handleOnChange}
          hasError={authError}
          errorMsg={authError?.message}
        />
        <Styled.PassInput
          label={t('Auth.signIn.labelPassword')}
          id="password"
          name="password"
          type="password"
          value={values.password.value}
          onChange={value => handleOnChange({ id: 'password', value: { value } })}
          hasBorder
          hasError={authError}
          testID="password"
        />
        <FlexContainer direction="column" gap="dimension_16" align="flex-start">
          <Buttons.Tiny
            label={t('Auth.signIn.recoverPassword')}
            onClick={onForgotPassword}
            mode="default"
            color="dark_500"
            disabled={!values.email.isValid}
          />
          <Buttons.Classic
            label={t('Auth.signIn.button')}
            type="submit"
            testID="signUpSubmit"
            mode="primary"
            size="l"
            fullWidth
            disabled={!isValidForm}
          />
        </FlexContainer>
      </FlexContainer>
    </form>
  );
};

export default SignInForm;
